import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import type { PlainSecret } from "@/queries/secret-queries";
import { useCallback, useEffect, useId, useMemo, useState, type FC } from "react";

type RegistryCredentials = {
    username: string;
    password: string;
};

export type EditSecretFormOnSubmitInput = {
    secretValuePlaintext: string;
};

export const EditSecretForm: FC<{
    secret: PlainSecret;
    formName: string;
    onDisabledChange: (disabled: boolean) => void;
    onSubmit: (values: EditSecretFormOnSubmitInput) => Promise<void>;
}> = ({ secret, formName, onSubmit, onDisabledChange }) => {
    const nameId = useId();
    const secretId = useId();
    const registryHostId = useId();
    const registryUsernameId = useId();
    const registryPasswordId = useId();

    const [newSecretValue, setNewSecretValue] = useState<string>("");
    const [registryCredentials, setRegistryCredentials] = useState<RegistryCredentials>({
        username: "",
        password: "",
    });

    const isEcrRegistry = useMemo(() => {
        // Check if it's a container registry secret
        if (secret.mount?.case !== "containerRegistryBasicAuthHost") return false;

        const host = secret.mount.value;
        return host.includes(".dkr.ecr.") && host.includes(".amazonaws.com");
    }, [secret.mount]);

    const isGarRegistry = useMemo(() => {
        // Check if it's a container registry secret
        if (secret.mount?.case !== "containerRegistryBasicAuthHost") return false;

        const host = secret.mount.value;
        return host.endsWith("-docker.pkg.dev");
    }, [secret.mount]);

    // Create a type which narrows down the type of the mount
    type ContainerRegistrySecret = PlainSecret & {
        mount: Extract<PlainSecret["mount"], { case: "containerRegistryBasicAuthHost" }>;
    };

    // This uses the typescript "type predicates" feature to help typescript narrow the type of secret in a type-safe way - e.g. if you have a typo in that string "containerRegistryBasicAuthHost" then it won't compile.
    function isRegistrySecretPredicate(secret: PlainSecret): secret is ContainerRegistrySecret {
        return secret.mount?.case === "containerRegistryBasicAuthHost";
    }

    const isRegistrySecret = isRegistrySecretPredicate(secret);

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (isRegistrySecret) {
                const authStr = `${registryCredentials.username}:${registryCredentials.password}`;
                await onSubmit({ secretValuePlaintext: btoa(authStr) });
            } else {
                await onSubmit({ secretValuePlaintext: newSecretValue });
            }
        },
        [onSubmit, newSecretValue, registryCredentials, isRegistrySecret],
    );

    useEffect(() => {
        const isValid = () => {
            if (isRegistrySecret) {
                return registryCredentials.username && registryCredentials.password;
            }
            return newSecretValue !== "";
        };

        onDisabledChange(!isValid());
    }, [newSecretValue, registryCredentials, onDisabledChange, isRegistrySecret]);

    const renderSecretInput = () => {
        if (isRegistrySecret) {
            return (
                <div className="flex flex-col gap-4">
                    <InputField id={registryHostId} label="Registry hostname">
                        <Input
                            id={registryHostId}
                            type="text"
                            name="registry-hostname"
                            value={secret.mount?.value}
                            disabled={true}
                            data-testid="registry-hostname-input"
                        />
                    </InputField>

                    {isEcrRegistry && (
                        <div className="flex flex-row gap-3 rounded-xl border border-border-base bg-surface-glass px-3 py-4">
                            <IconInfo size="base" className="text-blue-500" />
                            <div className="flex w-full grow flex-col">
                                <p className="text-base">
                                    ECR registry detected. Credentials set to use IAM authentication and cannot be
                                    modified.
                                </p>
                                <p className="text-sm">
                                    <ExternalLink href="https://ona.com/docs/ona/configuration/secrets/container-registry-secret#using-aws-ecr-with-iam-authentication">
                                        Learn more about ECR authentication
                                    </ExternalLink>
                                </p>
                            </div>
                        </div>
                    )}
                    {isGarRegistry && (
                        <div className="flex flex-row gap-3 rounded-xl border border-border-base bg-surface-glass px-3 py-4">
                            <IconInfo size="base" className="text-blue-500" />
                            <div className="flex w-full grow flex-col">
                                <p className="text-base">
                                    Google Artifact Registry (GAR) detected. Credentials set to use service account
                                    authentication and cannot be modified.
                                </p>
                                <p className="text-sm">
                                    <ExternalLink href="https://ona.com/docs/ona/configuration/secrets/container-registry-secret#using-google-artifact-registry-with-service-account-authentication">
                                        Learn more about GAR authentication
                                    </ExternalLink>
                                </p>
                            </div>
                        </div>
                    )}
                    <InputField id={registryUsernameId} label="Registry username">
                        <Input
                            id={registryUsernameId}
                            type="text"
                            name="registry-username"
                            value={registryCredentials.username}
                            placeholder="Enter registry username"
                            onChange={(e) => setRegistryCredentials((prev) => ({ ...prev, username: e.target.value }))}
                            autoComplete="off"
                            data-testid="registry-username-input"
                            disabled={isEcrRegistry || isGarRegistry}
                        />
                    </InputField>
                    <InputField id={registryPasswordId} label="Registry password">
                        <Input
                            id={registryPasswordId}
                            type="password"
                            name="registry-password"
                            value={registryCredentials.password}
                            placeholder="Enter registry password"
                            onChange={(e) => setRegistryCredentials((prev) => ({ ...prev, password: e.target.value }))}
                            autoComplete="off"
                            data-testid="registry-password-input"
                            disabled={isEcrRegistry || isGarRegistry}
                        />
                    </InputField>
                </div>
            );
        }

        return (
            <InputField id={secretId} label="Secret">
                <Textarea
                    id={secretId}
                    name="secret-value"
                    rows={5}
                    dontAutosize={true}
                    value={newSecretValue}
                    placeholder="Enter new secret value (current value is hidden for security)"
                    onChange={(e) => setNewSecretValue(e.target.value)}
                    className="resize-none"
                    data-testid="secret-value-input"
                />
            </InputField>
        );
    };

    return (
        <form
            onSubmit={handleSubmit}
            name={formName}
            id={formName}
            data-testid="edit-secret-form"
            className="flex flex-col gap-4"
        >
            <InputField label="Name" id={nameId} disabled={true}>
                <Input
                    id={nameId}
                    type="text"
                    name="secret-name"
                    value={secret.name}
                    disabled={true}
                    data-testid="secret-name-input"
                />
            </InputField>

            {/* Show secret type and file path as disabled fields for consistency */}
            <InputField label="Secret type" disabled={true}>
                <Input
                    type="text"
                    value={
                        isRegistrySecret
                            ? "Container registry"
                            : secret.mount?.case === "filePath"
                              ? "File"
                              : secret.mount?.case === "apiOnly"
                                ? "API only"
                                : "Environment variable"
                    }
                    disabled={true}
                    data-testid="secret-type-display"
                />
            </InputField>

            {secret.mount?.case === "filePath" && (
                <InputField label="File Location" disabled={true}>
                    <Input
                        type="text"
                        value={secret.mount.value}
                        disabled={true}
                        data-testid="secret-filepath-display"
                    />
                </InputField>
            )}

            {isRegistrySecret ? (
                renderSecretInput()
            ) : (
                <div className="flex flex-col gap-1">
                    {renderSecretInput()}
                    {/* Add helper text explaining security behavior */}
                    <div className="flex items-center gap-2 rounded-lg border border-border-base bg-surface-glass px-3 py-2">
                        <IconInfo size="sm" className="flex-shrink-0 text-blue-500" />
                        <p className="text-sm text-content-secondary">
                            Current value is hidden for security. To change file path or secret type, create a new
                            secret instead.
                        </p>
                    </div>
                </div>
            )}
        </form>
    );
};
